import type { LocationQuery, LocationQueryRaw, RouteLocationNormalized } from 'vue-router'
import type { TreeKind } from '@/types/extractions'

/** Public queue state is independent of migration/provider selection. */
export function extractionQueueQuery(query: LocationQuery): LocationQueryRaw {
  const result: LocationQueryRaw = {}
  for (const key of [
    'type',
    'assignee',
    'assignees',
    'search',
    'review',
    'sort',
    'page',
    'pageSize',
  ]) {
    if (query[key] !== undefined) result[key] = query[key]
  }
  return result
}

export function nativeExtractionTarget(
  projectId: number | string,
  fileId: string,
  kind: TreeKind,
  query: LocationQuery = {},
) {
  const queueQuery = extractionQueueQuery(query)
  return {
    name: kind === 'channel' ? 'project-extraction-channel' : 'project-extraction-assay',
    params: { id: String(projectId), fileId },
    ...(Object.keys(queueQuery).length ? { query: queueQuery } : {}),
  }
}

/** Query state owned by the canonical source-file workspace. */
export function fileWorkspaceQuery(query: LocationQuery): LocationQueryRaw {
  const result: LocationQueryRaw = {}
  for (const key of ['q', 'scope_id']) {
    const value = query[key]
    if (typeof value === 'string' && value.trim()) result[key] = value.trim()
  }
  return result
}

/** Historical /files links that pointed at an extraction certificate remain usable. */
export function legacyFilesTarget(to: { params: Record<string, unknown>; query: LocationQuery }) {
  const extractionId = to.query.extraction_id
  const certificate = to.query.cert
  if (
    typeof extractionId !== 'string' ||
    !extractionId ||
    typeof certificate !== 'string' ||
    !certificate
  )
    return true
  return {
    name: 'project-extraction-cert',
    params: { id: String(to.params.id), eid: extractionId, cert: certificate },
    query: {
      ...extractionQueueQuery(to.query),
      data_type: to.query.data_type ?? 'assays',
    },
  }
}

export function cleanFileWorkspaceLocation(
  to: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
) {
  if (
    to.name !== 'project-files' &&
    to.name !== 'project-file' &&
    to.name !== 'customer-files' &&
    to.name !== 'customer-file'
  )
    return null
  const query = fileWorkspaceQuery(to.query)
  const currentKeys = Object.keys(to.query).sort()
  const cleanKeys = Object.keys(query).sort()
  const unchanged =
    currentKeys.length === cleanKeys.length &&
    cleanKeys.every((key) => to.query[key] === query[key])
  return unchanged ? null : { name: to.name, params: to.params, query }
}

export function cleanExtractionLocation(
  to: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
) {
  if (typeof to.name !== 'string' || !to.name.startsWith('project-extraction')) return null
  if (
    to.query.provider === undefined &&
    to.query.read_only === undefined &&
    to.query.run === undefined &&
    to.query.run_id === undefined
  )
    return null
  const query = extractionQueueQuery(to.query)
  if (to.name === 'project-extraction-cert' && to.query.data_type !== undefined) {
    query.data_type = to.query.data_type
  }
  return { name: to.name, params: to.params, query }
}

export function extractionCompatibilityGuard(
  to: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
) {
  if (to.name === 'project-files') {
    const target = legacyFilesTarget(to)
    if (target !== true) return target
  }
  const cleanFileLocation = cleanFileWorkspaceLocation(to)
  if (cleanFileLocation) return cleanFileLocation
  const clean = cleanExtractionLocation(to)
  if (clean) return clean
  // These routes mount only the entry boundary. Historical certificate routes
  // are resolved to exact materialized V2 files or an actionable error.
  if (to.name === 'project-extraction-assay' || to.name === 'project-extraction-channel')
    return true
  return null
}
